import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { InputTextarea } from 'primereact/inputtextarea';
import { Message } from 'primereact/message';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Receipt, Material } from '../types';
import { receiptsAPI, materialsAPI } from '../services/api';

const Receipts: React.FC = () => {
  const [receipts, setReceipts] = useState<Receipt[]>([]);
  const [materials, setMaterials] = useState<Material[]>([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingReceipt, setEditingReceipt] = useState<Receipt | null>(null);
  const [formData, setFormData] = useState({
    materialId: '',
    receiptDate: new Date(),
    receiptTime: '',
    transporter: '',
    quantity: 0,
    unit: 'kg',
    notes: ''
  });
  const [error, setError] = useState('');
  const toast = useRef<Toast>(null);

  const unitOptions = [
    { label: 'kg', value: 'kg' },
    { label: 'g', value: 'g' },
    { label: 't', value: 't' }
  ];

  // Generate time options for dropdown (00:00 to 23:30 in 30-minute intervals)
  const timeOptions = useMemo(() => {
    const options = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        options.push({ label: timeString, value: timeString });
      }
    }
    return options;
  }, []);

  const materialOptions = useMemo(() => {
    return materials.map(material => ({
      label: material.name,
      value: material._id
    }));
  }, [materials]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [receiptsData, materialsData] = await Promise.all([
        receiptsAPI.getAll(),
        materialsAPI.getAll()
      ]);
      setReceipts(receiptsData);
      setMaterials(materialsData);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleAdd = () => {
    setEditingReceipt(null);
    setFormData({
      materialId: '',
      receiptDate: new Date(),
      receiptTime: '',
      transporter: '',
      quantity: 0,
      unit: 'kg',
      notes: ''
    });
    setError('');
    setModalVisible(true);
  };

  const handleEdit = (receipt: Receipt) => {
    setEditingReceipt(receipt);
    setFormData({
      materialId: receipt.materialId,
      receiptDate: new Date(receipt.receiptDate),
      receiptTime: receipt.receiptTime,
      transporter: receipt.transporter,
      quantity: receipt.quantity,
      unit: receipt.unit,
      notes: receipt.notes || ''
    });
    setError('');
    setModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await receiptsAPI.delete(id);
      toast.current?.show({
        severity: 'success',
        summary: 'Uspešno',
        detail: 'Prijemnica je uspešno obrisana',
        life: 3000
      });
      fetchData();
    } catch (error: any) {
      console.error("Error deleting receipt:", error);
      const errorMessage = error.response?.data?.message || "Greška pri brisanju prijemnice";
      toast.current?.show({
        severity: 'error',
        summary: 'Greška',
        detail: errorMessage,
        life: 5000
      });
    }
  };

  const handleSubmit = async () => {
    try {
      setError('');
      
      const receiptData = {
        ...formData,
        receiptDate: formData.receiptDate.toISOString().split('T')[0]
      };
      
      if (editingReceipt) {
        await receiptsAPI.update(editingReceipt._id, receiptData);
      } else {
        await receiptsAPI.create(receiptData);
      }

      setModalVisible(false);
      fetchData();
      
      toast.current?.show({
        severity: 'success',
        summary: 'Uspešno',
        detail: editingReceipt ? 'Prijemnica je uspešno ažurirana' : 'Prijemnica je uspešno kreirana',
        life: 3000
      });
    } catch (error: any) {
      setError(error.response?.data?.message || 'Greška pri čuvanju prijemnice');
    }
  };

  const actionsBodyTemplate = (rowData: Receipt) => {
    return (
      <div className="flex gap-2">
        <Button 
          icon="pi pi-pencil" 
          className="p-button-text p-button-sm"
          onClick={() => handleEdit(rowData)}
        />
        <Button 
          icon="pi pi-trash" 
          className="p-button-text p-button-sm p-button-danger"
          onClick={() => {
            confirmDialog({
              message: 'Da li ste sigurni da želite da obrišete ovu prijemnicu?',
              header: 'Potvrda brisanja',
              icon: 'pi pi-exclamation-triangle',
              accept: () => handleDelete(rowData._id)
            });
          }}
        />
      </div>
    );
  };

  const dateBodyTemplate = (rowData: Receipt) => {
    return new Date(rowData.receiptDate).toLocaleDateString('sr-RS');
  };

  const quantityBodyTemplate = (rowData: Receipt) => {
    return `${rowData.quantity} ${rowData.unit}`;
  };

  return (
    <div className="p-4">
      <Toast ref={toast} />
      <div className="flex justify-content-between align-items-center mb-4">
        <h2>Upravljanje prijemnicama</h2>
        <div className="flex gap-2">
          <Button 
            icon="pi pi-refresh" 
            onClick={fetchData} 
            className="p-button-outlined"
          />
          <Button 
            label="Dodaj prijemnicu" 
            icon="pi pi-plus" 
            onClick={handleAdd}
            severity="info"
          />
        </div>
      </div>

      <Card title="Prijemnice materijala">
        <DataTable
          value={receipts}
          paginator
          rows={10}
          emptyMessage="Nema prijemnica"
          className="p-datatable-sm"
        >
          <Column field="materialName" header="Materijal" />
          <Column 
            field="receiptDate" 
            header="Datum" 
            body={dateBodyTemplate}
          />
          <Column field="receiptTime" header="Vreme" />
          <Column field="transporter" header="Prevoznik" />
          <Column 
            field="quantity" 
            header="Količina" 
            body={quantityBodyTemplate}
          />
          <Column field="notes" header="Napomene" />
          <Column 
            field="createdBy" 
            header="Uneo" 
            body={(rowData: Receipt) => 
              rowData.createdBy ? `${rowData.createdBy.firstName} ${rowData.createdBy.lastName}` : 'N/A'
            }
          />
          <Column header="Akcije" body={actionsBodyTemplate} />
        </DataTable>
      </Card>

      <Dialog
        header={editingReceipt ? 'Izmeni prijemnicu' : 'Dodaj novu prijemnicu'}
        visible={modalVisible}
        onHide={() => setModalVisible(false)}
        style={{ width: '600px' }}
      >
        {error && (
          <Message severity="error" text={error} className="mb-3" />
        )}
        
        <div className="grid">
          <div className="col-12">
            <div className="field">
              <label htmlFor="materialId" className="block mb-2">Materijal *</label>
              <Dropdown
                id="materialId"
                value={formData.materialId}
                options={materialOptions}
                onChange={(e) => setFormData({...formData, materialId: e.value})}
                className="w-full"
                placeholder="Izaberite materijal"
                appendTo="self"
              />
            </div>
          </div>
          
          <div className="col-6">
            <div className="field">
              <label htmlFor="receiptDate" className="block mb-2">Datum prijemnice *</label>
              <Calendar
                id="receiptDate"
                value={formData.receiptDate}
                onChange={(e) => setFormData({...formData, receiptDate: e.value as Date})}
                className="w-full"
                dateFormat="dd.mm.yy"
                showIcon
              />
            </div>
          </div>
          
          <div className="col-6">
            <div className="field">
              <label htmlFor="receiptTime" className="block mb-2">Vreme prijemnice *</label>
              <Dropdown
                id="receiptTime"
                value={formData.receiptTime}
                options={timeOptions}
                onChange={(e) => setFormData({...formData, receiptTime: e.value})}
                className="w-full"
                placeholder="Izaberite vreme"
                appendTo="self"
              />
            </div>
          </div>
          
          <div className="col-12">
            <div className="field">
              <label htmlFor="transporter" className="block mb-2">Prevoznik</label>
              <InputText
                id="transporter"
                value={formData.transporter}
                onChange={(e) => setFormData({...formData, transporter: e.target.value})}
                className="w-full"
                placeholder="Unesite naziv prevoznika"
              />
            </div>
          </div>
          
          <div className="col-8">
            <div className="field">
              <label htmlFor="quantity" className="block mb-2">Količina *</label>
              <InputNumber
                id="quantity"
                value={formData.quantity}
                onValueChange={(e) => setFormData({...formData, quantity: e.value || 0})}
                className="w-full"
                min={0}
                suffix={` ${formData.unit}`}
              />
            </div>
          </div>
          
          <div className="col-4">
            <div className="field">
              <label htmlFor="unit" className="block mb-2">Jedinica</label>
              <Dropdown
                id="unit"
                value={formData.unit}
                options={unitOptions}
                onChange={(e) => setFormData({...formData, unit: e.value})}
                className="w-full"
                appendTo="self"
              />
            </div>
          </div>
          
          <div className="col-12">
            <div className="field">
              <label htmlFor="notes" className="block mb-2">Napomene</label>
              <InputTextarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                className="w-full"
                rows={3}
                placeholder="Dodatne napomene..."
              />
            </div>
          </div>
          
          <div className="col-12 flex justify-content-end gap-2">
            <Button 
              label={editingReceipt ? 'Ažuriraj' : 'Kreiraj'}
              onClick={handleSubmit}
              severity="info"
            />
            <Button 
              label="Otkaži"
              onClick={() => setModalVisible(false)}
              className="p-button-outlined"
            />
          </div>
        </div>
      </Dialog>

      <ConfirmDialog />
    </div>
  );
};

export default Receipts;
